import { normalizarTexto } from './texto';
/**
 * Condición frente al IVA de proveedores y clientes. Espejo de backend/src/common/condicion-iva.ts
 * (ahí están las pruebas): la lista, las etiquetas y la sugerencia desde el régimen fiscal tienen que coincidir.
 */

export const CONDICIONES_IVA = ['RESPONSABLE_INSCRIPTO', 'MONOTRIBUTO', 'EXENTO', 'CONSUMIDOR_FINAL', 'NO_ALCANZADO'] as const;
export type CondicionIva = (typeof CONDICIONES_IVA)[number];

export const ETIQUETAS_CONDICION_IVA: Record<CondicionIva, string> = {
  RESPONSABLE_INSCRIPTO: 'Responsable Inscripto',
  MONOTRIBUTO: 'Monotributo',
  EXENTO: 'Exento',
  CONSUMIDOR_FINAL: 'Consumidor Final',
  NO_ALCANZADO: 'No alcanzado',
};

/** La empresa se asume Responsable Inscripta (no hay dónde configurarlo). */
export const CONDICION_IVA_EMPRESA: CondicionIva = 'RESPONSABLE_INSCRIPTO';

export const esCondicionIva = (v: unknown): v is CondicionIva =>
  typeof v === 'string' && (CONDICIONES_IVA as readonly string[]).includes(v);

/** Etiqueta para mostrar; null si no hay condición o el código no es de la lista. */
export const etiquetaCondicionIva = (v: string | null | undefined): string | null =>
  esCondicionIva(v) ? ETIQUETAS_CONDICION_IVA[v] : null;

const CUIT_REGEX = /^\d{2}-?\d{8}-?\d{1}$/;
const esDni = (valor: string) => /^\d{7,8}$/.test(valor.replace(/[.\s-]/g, ''));

/**
 * Espejo de errorIdentificacionCliente (backend/src/common/validators/cuit.validator.ts): un consumidor final o
 * un cliente sin condición puede tener CUIT o DNI de 7 u 8 dígitos; el resto, CUIT. null si sirve o está vacío.
 */
export function errorIdentificacionCliente(cuit: string, condicionIva: string): string | null {
  if (!cuit || CUIT_REGEX.test(cuit)) return null;
  if (!condicionIva || condicionIva === 'CONSUMIDOR_FINAL') {
    return esDni(cuit) ? null : 'Para un consumidor final ingresá un CUIT válido o un DNI de 7 u 8 dígitos';
  }
  if (!esDni(cuit)) return 'CUIT inválido';
  return `Un cliente ${etiquetaCondicionIva(condicionIva) ?? condicionIva} necesita CUIT`;
}

const normalizar = normalizarTexto;

const SUGERENCIAS: [CondicionIva, string[]][] = [
  ['RESPONSABLE_INSCRIPTO', ['responsable inscripto', 'resp inscripto', 'ri', 'r i']],
  ['MONOTRIBUTO', ['monotributo', 'monotributista']],
  ['EXENTO', ['exento']],
];

/** Condición sugerida desde el régimen fiscal (texto libre) cuando el texto completo coincide claramente; si no, null. */
export function sugerirCondicionIva(regimenFiscal: string | null | undefined): CondicionIva | null {
  if (!regimenFiscal) return null;
  const texto = normalizar(regimenFiscal).replace(/^iva /, '');
  return SUGERENCIAS.find(([, textos]) => textos.includes(texto))?.[0] ?? null;
}
